import { ClockPort, HostMessageEvent, LoggerPort, MessagePort } from './ports.types';

/**
 * Default port implementations.
 *
 * Every one of these is created lazily by a function rather than evaluated at
 * module scope, so importing this library never touches `window`. That keeps
 * the package importable from a Node test, an SSR render, or a web worker.
 */

export function createBrowserClock(): ClockPort {
  return { now: () => Date.now() };
}

/** A logger that discards everything. The default: a library should be quiet. */
export function createNoopLogger(): LoggerPort {
  return {
    debug: () => undefined,
    info: () => undefined,
    warn: () => undefined,
    error: () => undefined,
  };
}

export function createConsoleLogger(prefix = '[auth-core]'): LoggerPort {
  return {
    debug: (message, context) => console.debug(prefix, message, context ?? ''),
    info: (message, context) => console.info(prefix, message, context ?? ''),
    warn: (message, context) => console.warn(prefix, message, context ?? ''),
    error: (message, context) => console.error(prefix, message, context ?? ''),
  };
}

export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.addEventListener === 'function';
}

/**
 * Wraps `window` as a {@link MessagePort}.
 *
 * `postToHost` targets `window.parent` and no-ops when the page is not framed,
 * so a host handshake attempted in a top-level window fails by timing out
 * rather than by throwing somewhere unexpected.
 */
export function createWindowMessagePort(): MessagePort {
  const listeners = new WeakMap<(event: HostMessageEvent) => void, EventListener>();

  return {
    addEventListener: (_type, listener) => {
      const wrapped: EventListener = (event) => {
        const messageEvent = event as MessageEvent;
        listener({
          origin: messageEvent.origin,
          data: messageEvent.data,
          source: messageEvent.source,
        });
      };
      listeners.set(listener, wrapped);
      window.addEventListener('message', wrapped);
    },
    removeEventListener: (_type, listener) => {
      const wrapped = listeners.get(listener);
      if (!wrapped) return;
      window.removeEventListener('message', wrapped);
      listeners.delete(listener);
    },
    postToHost: (message, targetOrigin) => {
      if (window.parent === window) return;
      window.parent.postMessage(message, targetOrigin);
    },
    isHostWindow: (source) => window.parent !== window && source === window.parent,
  };
}
